import React, {
  ReactNode,
  forwardRef,
  useState,
  useMemo,
  useEffect,
  useImperativeHandle,
} from 'react';
import get from 'lodash/get';
import { CnIcon, CnPopover } from '@cainiaofe/cn-ui-m';
import { CnPagination } from '@cainiaofe/cn-ui';
import { CnMiniTable as UICnMiniTable } from '@/components/cn-mini-table';
import { isDesignMode } from '@/lowcode/common/util/util';
import useFilterSearchListener from '../common/hooks/useFilterSearchListener';
import { DqiItem } from '@/lowcode/common/setter/components/drag-item/types';
import useDependencyEffect from '@/lowcode/common/hooks/useDependencyEffect';
import { META_CONFIG } from '@/lowcode/common/util/const';
import { EmptyData } from '../common/empty-data';
import { getFormattedValue } from '@/lowcode/common/comoponents/util';
import WrapperContainer from '../common/wrapper-container';
import { useData } from '@/lowcode/common/hooks/useData';
import { SortItem } from '@/lowcode/common/types';
import Skeleton from './skeleton';

import './view.scss';

const CnDataTable = forwardRef((props: any, ref: React.ForwardedRef<{ load?: () => void }>) => {
  const {
    dataFrom,
    _context,
    filterConfig,
    chartStyle = {},
    pagination = {},
    forceRequest,
    skeleton = {},
  } = props || {};
  const { requestConfig } = dataFrom || {};
  const { _bindFilter } = filterConfig || {};
  const records = (get(requestConfig, `${META_CONFIG}.columns.records`) || []) as DqiItem[];
  const initSorts = records.map((item) => {
    const { sortConfig = {} } = item.settings || {};
    if (sortConfig.open) {
      return {
        resultName: item.name,
        order: sortConfig.sortType,
      };
    }
    return { resultName: item.name };
  });

  const [prevSorts, setPrevSorts] = useState<SortItem[]>(initSorts as SortItem[]);
  const [sorts, setSorts] = useState<SortItem[]>(initSorts as SortItem[]);
  // 当排序配置发生变化时重置排序(只有在编辑态下才会重置)
  if (JSON.stringify(initSorts) !== JSON.stringify(prevSorts)) {
    setPrevSorts(initSorts);
    setSorts(initSorts);
  }

  const [current, setCurrent] = useState<number>(1);

  const {
    data: tableData,
    paging,
    error,
    loading,
    refreshData: sendRequest,
  } = useData({
    config: {
      ...props,
      sorts,
      isDesign: isDesignMode(props),
      ...(pagination?.open ? { currentPage: current, pageSize: pagination?.pageSize } : null),
    },
    source: 'chart',
  });

  useFilterSearchListener({
    _bindFilter,
    sendRequest,
    _context,
  });

  useEffect(() => {
    if (forceRequest) {
      sendRequest();
    }
  }, [forceRequest]);

  useImperativeHandle(ref, () => ({
    load: sendRequest,
  }));

  useDependencyEffect(() => {
    sendRequest();
  }, [sorts]);

  useDependencyEffect(() => {
    sendRequest();
  }, [current]);

  const tableColumns = useMemo(() => {
    return records
      .map((item) => {
        const {
          aliasName,
          tips,
          fixed,
          width,
          ellipsis,
          sortConfig = {},
          cell = {},
          conditional = {},
          align = 'left',
          columnHidden,
        } = item.settings || {};
        const { getJumpUrl, formatValue } = cell;
        const { getFontColor } = conditional;
        const { open, sortType } = sortConfig;

        // 控制列隐藏
        if (getFormattedValue(columnHidden)) {
          return null;
        }

        const Title = ({ onChange }: { onChange: (type: 'DESC' | 'ASC') => void }) => {
          const [sortTypeKey, setSortTypeKey] = useState<'DESC' | 'ASC' | undefined>(sortType);
          const justifyContentMap: Record<string, string> = {
            left: 'flex-start',
            right: 'flex-end',
            center: 'center',
          };
          const renderTips = () => {
            if (!tips) {
              return null;
            }
            return (
              <CnPopover content={tips} trigger="click" placement="bottom" mode="dark">
                <CnIcon type="help-color" size="medium" style={{ marginLeft: '5px' }} />
              </CnPopover>
            );
          };

          const sortClick = () => {
            if (!sortTypeKey) {
              setSortTypeKey('DESC');
              onChange?.('DESC');
            }
            if (sortTypeKey === 'ASC') {
              setSortTypeKey('DESC');
              onChange?.('DESC');
            }
            if (sortTypeKey === 'DESC') {
              setSortTypeKey('ASC');
              onChange?.('ASC');
            }
          };

          const renderSort = () => {
            if (!open) {
              return null;
            }
            let iconDom: ReactNode = (
              <CnIcon
                type="triangle-sort"
                size="medium"
                style={{ marginLeft: '5px', color: '#999999' }}
                color="#999999"
              />
            );
            if (sortTypeKey === 'DESC') {
              iconDom = (
                <CnIcon
                  type="triangle-sort-descending"
                  onClick={sortClick}
                  size="medium"
                  style={{ marginLeft: '5px' }}
                />
              );
            }
            if (sortTypeKey === 'ASC') {
              iconDom = (
                <CnIcon
                  type="triangle-sort-ascending"
                  size="medium"
                  style={{ marginLeft: '5px' }}
                />
              );
            }
            return (
              <div style={{ display: 'flex', alignItems: 'center' }} onClick={sortClick}>
                {iconDom}
              </div>
            );
          };

          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                ...(align ? { justifyContent: justifyContentMap[align] } : null),
              }}
            >
              {getFormattedValue(aliasName || item.name, item.name)}
              {renderTips()}
              {renderSort()}
            </div>
          );
        };

        /* const formatUrl = (row: any) => {
        const replacedStr = jumpUrl.replace(/\${(.*?)}/g, (match: string, field: string) => {
          // 根据字段名进行相应的处理
          if (row[field]) {
            return row[field];
          } else {
            return match;
          }
        });
        return replacedStr;
      }; */

        const renderCell = (itemConfig: any, o: string, row: any, index: number) => {
          const { options, format } = itemConfig?.settings || {};
          let color: string;
          try {
            color = getFontColor?.(o, row);
          } catch (err) {
            console.error('函数：getFontColor 执行错误');
            console.error(err);
          }
          try {
            o = formatValue ? formatValue(o, row) : o;
          } catch (err) {
            console.error('函数：getFontColor 执行错误');
            console.error(err);
          }

          const renderText = () => {
            const CnFormat = (
              window?.CN_UI_LOWCODE || window?.['cn-ui-m-lowcode']
            )?.default?.find?.((com) => com.displayName === 'CnFormat');
            if (format && CnFormat) {
              return (
                <CnFormat
                  formatSchema={[
                    {
                      format,
                      options,
                    },
                  ]}
                  _context={_context}
                  __getContextData__={() => {
                    return {
                      functionArg: {
                        item: row,
                        index,
                        value: o,
                        state: _context?.state,
                        _context,
                      },
                    };
                  }}
                />
              );
            }
            return <span style={{ ...(color ? { color } : null) }}>{o}</span>;
          };

          let jumpUrl = '';

          if (getJumpUrl) {
            try {
              jumpUrl = getJumpUrl(row);
            } catch (e) {
              console.error(e);
            }
          }

          if (jumpUrl) {
            return (
              <a
                href={jumpUrl}
                onClick={(e) => {
                  e.preventDefault();
                  window.open(jumpUrl, '_blank');
                  // navigateTo({ url: jumpUrl });
                }}
              >
                {renderText()}
              </a>
            );
          }
          if (ellipsis) {
            return (
              <CnPopover content={o} trigger="click" placement="bottom" mode="dark">
                {renderText()}
              </CnPopover>
            );
          }

          return renderText();
        };

        return {
          title: (
            <Title
              onChange={(type: 'DESC' | 'ASC') => {
                const newSorts = sorts.map((sort) => {
                  if (sort.resultName === item.name) {
                    return {
                      ...sort,
                      order: type,
                    };
                  }
                  return { ...sort };
                });
                setSorts(newSorts);
              }}
            />
          ),
          dataIndex: item.name,
          ...(ellipsis
            ? {
                ellipsis: {
                  showTitle: false,
                },
              }
            : null),

          ...(width ? { width } : null),
          ...(fixed ? { fixed } : null),
          ...(align ? { align } : null),
          render: renderCell.bind(null, item),
        };
      })
      .filter((item) => !!item);
  }, [records]);

  if (!tableColumns || !tableColumns.length) {
    return (
      <EmptyData
        height={chartStyle.height}
        emptyImg={
          'https://gw.alicdn.com/imgextra/i2/O1CN01HSYuts1zuXpPAbvTw_!!6000000006774-2-tps-500-260.png'
        }
      />
    );
  }

  return (
    <WrapperContainer
      loading={loading}
      skeletonNode={<Skeleton contentLength={skeleton.contentLength} />}
      skeletonVisible={!tableData}
      error={error}
    >
      <UICnMiniTable
        style={{ width: '100%' }}
        scroll={{
          x: 0,
          ...(chartStyle.height ? { y: chartStyle.height } : null),
        }}
        columns={tableColumns}
        data={tableData || []}
      />
      {pagination?.open ? (
        <CnPagination
          className="mini-table-pagination"
          current={current}
          total={paging?.totalCount || 0}
          pageSize={pagination?.pageSize || 10}
          // size="small"
          onChange={(val: number) => setCurrent(val)}
        />
      ) : null}
    </WrapperContainer>
  );
});

CnDataTable.displayName = 'CnDataTable';
export default CnDataTable;

export { CnDataTable };
